import { defineStore } from 'pinia';
import {
  computed,
  ref,
  useRuntimeConfig,
  navigateTo,
  nextTick,
  useCookie,
  type Ref,
} from '#imports';
import {
  asAuthUser,
  runLogoutFlow,
  usePermissionChecker,
  hasRole,
  hasMatch,
  getAuthCookieOptions,
} from 'r3ui-lib';
import { fetchAuthUser, logOutSSO } from '~/services/auth.service';
import {
  isUnauthorizedError,
  presentPageLoadError,
} from '~/utils/presentPageLoadError';

export const useAuthStore = defineStore(
  'auth',
  () => {
    const config = useRuntimeConfig();
    const { authApiUrl, origin } = config.public;
    // Опции обязаны совпадать с теми, с которыми cookie ставит серверный
    // callback: без domain удаление не снимет cookie родительского домена.
    const cookie = useCookie<string | null>(
      'auth_token',
      getAuthCookieOptions({
        origin: origin as string | undefined,
        authCookieDomain: config.public.authCookieDomain as string | undefined,
      })
    ) as Ref<string | null>;
    const token = ref<string | null | undefined>(cookie.value);
    const user = ref(null);
    let fetchVersion = 0;

    const isAuthenticated = computed(() => {
      return Boolean(token.value);
    });

    const hasUserPermission = usePermissionChecker(
      computed(() => user.value?.all_permissions ?? [])
    );

    const userRoleNames = computed(() => {
      if (!Array.isArray(user.value?.roles)) {
        return [];
      }

      return user.value.roles
        .map((role: { name?: string }) => role?.name)
        .filter((roleName: string | undefined): roleName is string =>
          Boolean(roleName)
        );
    });

    const hasUserRole = (role: string | string[]) => {
      if (Array.isArray(role)) {
        return hasMatch(role, userRoleNames.value);
      }

      return hasRole(userRoleNames.value, role);
    };

    async function fetchUser() {
      if (!token.value) return false;
      const requestedToken = token.value;
      const version = ++fetchVersion;
      const isCurrent = () =>
        version === fetchVersion && token.value === requestedToken;

      try {
        const loaded = await fetchAuthUser(requestedToken);
        if (!isCurrent()) return false;
        const userRecord = asAuthUser(loaded);
        if (!userRecord) {
          presentPageLoadError(new Error('fetchUser: пустой ответ'));
          return false;
        }
        user.value = userRecord;
        return true;
      } catch (error) {
        if (!isCurrent()) return false;
        if (isUnauthorizedError(error)) {
          resetAll();
          return false;
        }
        presentPageLoadError(error);
        return false;
      }
    }

    function resetStorage() {
      const storageLinks = ['breadcrumbs', 'filters', 'tables'];
      storageLinks.map(i => {
        localStorage.removeItem(i);
      });
    }

    function resetAll() {
      fetchVersion++;
      token.value = null;
      user.value = null;
      cookie.value = null;
    }

    async function logout() {
      fetchVersion++;
      const baseLogoutUrl = authApiUrl ? `${authApiUrl}/logout` : '/login';
      const redirectPath = origin
        ? `${baseLogoutUrl}?redirect=${encodeURIComponent(origin)}`
        : baseLogoutUrl;
      const navigateWithExternal = (url: string): void | Promise<void> => {
        if (/^https?:\/\//.test(url)) {
          return navigateTo(url, { external: true }) as void | Promise<void>;
        }
        return navigateTo(url) as void | Promise<void>;
      };
      await runLogoutFlow({
        logOutSSO,
        nextTick,
        navigateTo: navigateWithExternal,
        onReset: () => {
          resetAll();
          resetStorage();
        },
        onError: (error: unknown) => {
          // eslint-disable-next-line no-console
          console.error('[logout]', error);
        },
        redirectPath,
      });
    }

    return {
      isAuthenticated,
      logout,
      fetchUser,
      token,
      user,
      hasUserPermission,
      hasUserRole,
    };
  },
  {
    persist: {
      pick: ['user'],
    },
  }
);
